// Bagged-peaks CSVs in the Import files screen (spec/fixtures/import/README.md, "Bagged-peaks
// CSV"): recognised by a header that names a peak the way the server's Peaks::BagCsvParser
// does, or by simply looking like a table — the server reads columns it doesn't know with
// Claude — and sent to My Peaks from every start screen. Only recognising the file and
// counting its rows happens here; the server decides whether it's a log of peaks and matches
// the rows.
// Twins: iOS `ImportPeaksCsv.swift`, Android `ImportPeaksCsv.kt`.

import { emptyPreview } from './importRules';
import type { ImportPreview } from './importTypes';

/** The header cells, squashed, that identify a peak (Peaks::BagCsvParser::COLUMNS, less the date). */
export const PEAK_CSV_COLUMNS: ReadonlySet<string> = new Set([
  'hiikerpeakid', 'hiikerid', 'amenityid', 'peakid',
  'dobihnumber', 'dobih', 'hillnumber', 'hillno', 'number',
  'name', 'hillname', 'peakname', 'peak', 'hill', 'summit', 'mountain',
  'latitude', 'lat'
]);

const SEPARATORS = [',', ';', '\t'];

/** The file's text, without a BOM. */
export const csvText = (bytes: Uint8Array): string => new TextDecoder('utf-8').decode(bytes).replace(/^﻿/, '');

const lines = (text: string): string[] => text.split('\n').map(l => l.replace(/\r$/, ''));

/** Whichever of `,` `;` tab the header holds most of; the earlier one on a tie. */
export function csvSeparator(header: string): string {
  const count = (sep: string): number => header.split(sep).length - 1;
  return SEPARATORS.reduce((best, sep) => (count(sep) > count(best) ? sep : best));
}

/** "Hill Number" → "hillnumber", as the server matches headers. */
export const squashHeader = (cell: string): string => cell.trim().replace(/^"+|"+$/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Whether `text`'s first line is a bagged-peaks CSV header. */
export function isPeaksCsv(text: string): boolean {
  const header = lines(text)[0] ?? '';
  return header.split(csvSeparator(header)).some(cell => PEAK_CSV_COLUMNS.has(squashHeader(cell)));
}

/** How many cells `line` splits into on `sep`, a separator inside double quotes not counting. */
export function csvCellCount(line: string, sep: string): number {
  let cells = 1;
  let quoted = false;
  for (const ch of line) {
    if (ch === '"') quoted = !quoted;
    else if (ch === sep && !quoted) cells += 1;
  }
  return cells;
}

/**
 * Whether `text` looks like a table: among its first 10 lines that aren't blank, two in a
 * row split into the same number of cells, at least two, on whichever of `,` `;` tab those
 * lines hold most of. A title line above the table, or no header at all, still counts.
 */
export function looksTabular(text: string): boolean {
  const rows = lines(text).filter(l => l.trim() !== '').slice(0, 10);
  const sep = csvSeparator(rows.join('\n'));
  const counts = rows.map(row => csvCellCount(row, sep));
  return counts.some((count, i) => i > 0 && count >= 2 && count === counts[i - 1]);
}

/** Whether `text` is a CSV the server should be asked about: a peak header, or a table. Never binary. */
export const isCsv = (text: string): boolean => !text.includes('\u0000') && (isPeaksCsv(text) || looksTabular(text));

/** The lines after the first that aren't blank. */
export const peakCsvRows = (text: string): number => lines(text).slice(1).filter(l => l.trim() !== '').length;

/** A bagged-peaks CSV's preview: it can only go to My Peaks, and a header alone is empty. */
export function peaksCsvPreview(text: string): ImportPreview {
  const rows = peakCsvRows(text);
  if (rows === 0) return emptyPreview('csv', 'empty');
  return { ...emptyPreview('csv', null), canBe: ['peaks'], recommended: 'peaks', peakRows: rows };
}
